import { Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import { ThemeProvider } from "@material-ui/core/styles";
import makeStyles from "@material-ui/core/styles/makeStyles";
import TextField from "@material-ui/core/TextField";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Notification, useLogin, useNotify, useTranslate } from "react-admin";
import { connect } from "react-redux";

import Icon from "../assets/img/edding-logo.svg";
import { baseApi, redirectToMicrosoftSSO } from "../common/envUtils";
import PasswordTextField from "../components/PasswordTextField";
import {
	setPasswordAction,
	setTwoFactorAuthCode,
	setTwoFaEnabled,
	setUsernameAction,
} from "../redux/authReducer";
import { fetchSettings } from "../redux/settingsReducer";

const useStyles = makeStyles(() => ({
	logoImage: {
		objectFit: "scale-down",
		width: 445,
		height: 128,
	},
	titleContainer: {
		paddingBottom: 38,
		borderBottom: properties => `1px solid ${properties.secondaryColor}`,
	},
}));

const MyLoginPage = ({
	theme,
	username,
	password,
	onUsernameChange,
	onPasswordChange,
	history,
	twoFactorAuthCode,
	onTwoFactorAuthCodeChange,
	twoFaEnabled,
	onTwoFaEnabledChange,
	fetchUserSettings,
}) => {
	const classes = useStyles({ secondaryColor: theme.palette.secondary.main });
	const login = useLogin();
	const translate = useTranslate();
	const notify = useNotify();
	const [step, setStep] = useState("email"); // "email", "sso", "login"
	const [hasMicrosoftSso, sethasMicrosoftSso] = useState(false);
	const [checkingSso, setCheckingSso] = useState(false);

	useEffect(() => {
		const hash = window?.location?.hash;
		if (!hash) {
			return;
		}
		const hashParams = hash.split("?");
		if (!hashParams || hashParams.length < 2) {
			return;
		}
		const queryString = hashParams[1];
		const params = new URLSearchParams(queryString);
		const error = params.get("error");
		if (error) {
			const translatedError =
				translate(`ra.auth.${error}`, {
					_: translate("ra.auth.microsoft_login_failed"),
				}) || translate("ra.auth.microsoft_login_failed");
			notify(translatedError, { type: "warning" });
			params.delete("error");
			const cleanedQuery = params.toString();
			const newHash = cleanedQuery ? `${hashParams[0]}?${cleanedQuery}` : hashParams[0];
			window.history.replaceState(
				null,
				"",
				`${window.location.pathname}${window.location.search}${newHash}`
			);
		} else {
			const token = params.get("token");
			const user = params.get("user");

			if (token && user) {
				localStorage.setItem("token", token);
				localStorage.setItem("user", decodeURIComponent(user));
				fetchUserSettings();
				history.push("/");
			}
		}
	}, [fetchUserSettings, history, notify]);

	const submit = e => {
		e.preventDefault();
		login({ username, password, twoFactorAuthCode })
			.then(data => {
				onPasswordChange("");
				onUsernameChange("");
				if (data.require2FA) {
					notify(translate("ra.auth.require_2fa"));
					history.push("/settings");
				}
				fetchUserSettings();
				onTwoFaEnabledChange(false);
			})
			.catch(error => {
				if (error.body && error.body.twoFactorEnabled) {
					onTwoFaEnabledChange(true);
					if (twoFactorAuthCode) {
						notify(translate("ra.auth.invalid_2fa"));
					}
				} else if (
					error.body &&
					error.body.message === "User not assigned to any department"
				) {
					onTwoFaEnabledChange(false);
					notify(translate("ra.auth.not_assigned_any_department"));
				} else {
					onTwoFaEnabledChange(false);
					notify(translate("ra.auth.sign_in_error"));
				}
			});
	};

	const handlePasswordResetClick = () => {
		history.push("/auth/reset");
	};

	const handleContinue = async e => {
		if (e) {
			e.preventDefault();
		}
		if (!username) {
			notify(translate("ra.auth.username_required"), { type: "warning" });
			return;
		}

		setCheckingSso(true);
		try {
			const response = await axios.get(`${baseApi}/auth/check-sso`, {
				params: { email: username },
			});
			const { hasMicrosoftSso: ssoAvailable } = response.data;
			sethasMicrosoftSso(ssoAvailable);
			setStep(ssoAvailable ? "sso" : "login");
		} catch {
			// If check fails, default to password login
			sethasMicrosoftSso(false);
			setStep("login");
		} finally {
			setCheckingSso(false);
		}
	};

	const handleBackToEmail = () => {
		setStep("email");
		sethasMicrosoftSso(false);
		onPasswordChange("");
	};

	const handleMicrosoftSSO = () => {
		if (username) {
			redirectToMicrosoftSSO(`?email=${encodeURIComponent(username)}`);
		} else {
			redirectToMicrosoftSSO();
		}
	};

	return (
		<ThemeProvider theme={theme}>
			<Container
				fixed
				style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
			>
				<div style={{ flex: 1 }} />
				<Box
					display="flex"
					flexDirection="column"
					justifyContent="center"
					alignItems="center"
				>
					<Box
						height="80%"
						maxHeight={700}
						display="flex"
						flexDirection="column"
						justifyContent="space-around"
						alignItems="center"
					>
						<img className={classes.logoImage} src={Icon} alt="company_logo" />

						<Box className={classes.titleContainer}>
							<Typography variant="h1">{translate("pages.login.title")}</Typography>
							<Typography variant="h2">
								{translate("pages.login.subtitle")}
							</Typography>
						</Box>
						<form
							data-testid="login-form"
							autoComplete="on"
							onSubmit={e => {
								e.preventDefault();
								if (step === "email") {
									handleContinue(e);
								} else if (step === "login") {
									submit(e);
								}
								// SSO step doesn't submit the form, it redirects
							}}
						>
							<Box
								maxWidth={520}
								width="50vw"
								height={350}
								display="flex"
								flexDirection="column"
								flexWrap="wrap"
								justifyContent="start"
							>
								<Box my={1}>
									<Typography variant="subtitle1">Login</Typography>
								</Box>
								<Box my={2}>
									<TextField
										fullWidth
										label={translate("ra.auth.username")}
										name="username"
										type="email"
										value={username}
										variant="outlined"
										disabled={step !== "email"}
										autoComplete="username"
										InputProps={{
											inputProps: { "data-testid": "username-field" },
										}}
										onChange={e => onUsernameChange(e.target.value)}
									/>
								</Box>

								{/* Always render password field for autofill, but hide when on email or sso step */}
								<Box
									mb={2}
									style={{
										visibility:
											step === "email" || step === "sso"
												? "hidden"
												: "visible",
										height: step === "email" || step === "sso" ? 0 : "auto",
										overflow:
											step === "email" || step === "sso"
												? "hidden"
												: "visible",
										marginBottom: step === "email" || step === "sso" ? 0 : 16,
									}}
								>
									<PasswordTextField
										fullWidth
										label={translate("ra.auth.password")}
										name="password"
										value={password}
										variant="outlined"
										autoComplete="current-password"
										InputProps={{
											inputProps: {
												"data-testid": "password-field",
												autoComplete: "current-password",
											},
										}}
										onChange={e => onPasswordChange(e.target.value)}
									/>
								</Box>

								{step === "email" && (
									<Box display="flex" flexDirection="row" alignItems="center">
										<Button
											variant="contained"
											color="primary"
											type="submit"
											data-testid="continue-button"
											style={{ width: 140 }}
											disabled={!username || checkingSso}
										>
											{checkingSso
												? translate("ra.auth.checking")
												: translate("ra.auth.continue")}
										</Button>
										<Button
											color="primary"
											style={{ width: 180, marginLeft: 16 }}
											onClick={handlePasswordResetClick}
										>
											{translate("pages.login.passwordForget")}
										</Button>
									</Box>
								)}

								{step === "sso" && (
									<Box
										mb={2}
										display="flex"
										flexDirection="column"
										alignItems="flex-start"
									>
										<Box mb={1}>
											<Typography variant="body2">
												{translate("ra.auth.sso_info_message")}
											</Typography>
										</Box>
										<Box>
											<Button
												variant="contained"
												color="primary"
												data-testid="sso-button"
												style={{ width: 300 }}
												onClick={handleMicrosoftSSO}
											>
												{translate("ra.auth.sso_sign_in")}
											</Button>
											<Button
												color="primary"
												style={{ marginLeft: 16 }}
												onClick={handleBackToEmail}
											>
												{translate("ra.auth.back")}
											</Button>
										</Box>
									</Box>
								)}

								{step === "login" && (
									<>
										{twoFaEnabled && (
											<Box mb={2}>
												<TextField
													fullWidth
													name="twoFactorAuthCode"
													value={twoFactorAuthCode}
													variant="outlined"
													label={translate(
														"pages.settings.twoFa.twoFactorAuthCode"
													)}
													onChange={e =>
														onTwoFactorAuthCodeChange(e.target.value)
													}
												/>
											</Box>
										)}

										<Box display="flex" flexDirection="row" alignItems="center">
											<Button
												variant="contained"
												color="primary"
												type="submit"
												data-testid="submit-button"
												style={{ width: 140 }}
												disabled={!username || !password}
											>
												{translate("ra.auth.sign_in")}
											</Button>
											<Button
												color="primary"
												style={{ width: 180, marginLeft: 16 }}
												onClick={handlePasswordResetClick}
											>
												{translate("pages.login.passwordForget")}
											</Button>
											<Button
												color="primary"
												style={{ marginLeft: 16 }}
												onClick={handleBackToEmail}
											>
												{translate("ra.auth.back")}
											</Button>
										</Box>
									</>
								)}
							</Box>
						</form>
					</Box>
				</Box>
				<div style={{ flex: 1 }} />
			</Container>

			<Notification />
		</ThemeProvider>
	);
};

const mapStateToProperties = state => ({
	username: state.auth.username,
	password: state.auth.password,
	twoFactorAuthCode: state.auth.twoFactorAuthCode,
	twoFaEnabled: state.auth.twoFaEnabled,
});

const mapDispatchToProperties = dispatch => ({
	onUsernameChange: username => {
		dispatch(setUsernameAction(username));
	},
	onPasswordChange: password => {
		dispatch(setPasswordAction(password));
	},
	onTwoFactorAuthCodeChange: twoFaCode => {
		dispatch(setTwoFactorAuthCode(twoFaCode));
	},
	onTwoFaEnabledChange: twoFaEnabled => {
		dispatch(setTwoFaEnabled(twoFaEnabled));
	},
	fetchUserSettings: () => {
		dispatch(fetchSettings());
	},
});

export default connect(mapStateToProperties, mapDispatchToProperties)(MyLoginPage);
